// Frequency for a bill: one time, monthly, yearly, or every N days. Stored on the
// bill as repeatUnit + repeatEvery; the human-readable "schedule" label shown
// everywhere else in the app (tables, cards) is generated from these two.
export const BILL_FREQUENCIES = [
  { value: "once", label: "One time" },
  { value: "months", label: "Monthly" },
  { value: "years", label: "Yearly" },
  { value: "days", label: "Every few days" },
];

// Investments, "other" holdings, loans and income repeat every N days, months or years.
export const REPEAT_FREQUENCIES = [
  { value: "once", label: "Doesn't repeat" },
  { value: "days", label: "Days" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

export const scheduleLabel = (repeatUnit, repeatEvery) => {
  const n = repeatEvery || 1;
  if (repeatUnit === "months") return n === 1 ? "Monthly" : `Every ${n} months`;
  if (repeatUnit === "years") return n === 1 ? "Yearly" : `Every ${n} years`;
  if (repeatUnit === "days") return n === 1 ? "Daily" : `Every ${n} days`;
  return "One-time";
};

// Only repeating entries belong on Bills & Payments; one-time ones are expenses
// (or investments) and live in Activity / Transactions instead.
export const isRepeatingBill = (bill) => !!bill && !!bill.repeatUnit && bill.repeatUnit !== "once";

// "Every 3 [months]" / "Every 1 [month]" - the word after the number in the
// "repeats every" field.
export const repeatUnitWord = (unit, every) => {
  const word = { days: "day", months: "month", years: "year" }[unit] || "";
  return parseInt(every, 10) === 1 ? word : `${word}s`;
};

export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// The given day in that month, or its last day if the month is shorter (31 -> 30 Apr).
const dayIn = (year, month, day) => new Date(year, month, Math.min(day, new Date(year, month + 1, 0).getDate()));

// A monthly bill is entered as just a day of the month, a yearly one as a month and
// day. This is the first due date from those: the next time that day comes round,
// today included. `month` is 0-11. Returns YYYY-MM-DD.
export const nextDueDate = (repeatUnit, { day, month }, reference = new Date()) => {
  const today = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  if (repeatUnit === "years") {
    const thisYear = dayIn(today.getFullYear(), month, day);
    return isoOf(thisYear >= today ? thisYear : dayIn(today.getFullYear() + 1, month, day));
  }
  const thisMonth = dayIn(today.getFullYear(), today.getMonth(), day);
  return isoOf(thisMonth >= today ? thisMonth : dayIn(today.getFullYear(), today.getMonth() + 1, day));
};

// Income works the other way round: a salary "on the 1st" means this month's 1st,
// even if that has passed (you may already have it). This is that date - this
// month's day for monthly, this year's month and day for yearly. YYYY-MM-DD.
export const currentPeriodDate = (repeatUnit, { day, month }, reference = new Date()) =>
  isoOf(repeatUnit === "years" ? dayIn(reference.getFullYear(), month, day) : dayIn(reference.getFullYear(), reference.getMonth(), day));

// The next date after `iso` on the same schedule, as the server lines it up.
export const nextOccurrence = (iso, repeatUnit, repeatEvery = 1) => {
  const [y, m, d] = iso.split("-").map(Number);
  if (repeatUnit === "days") return isoOf(new Date(y, m - 1, d + repeatEvery));
  const months = repeatUnit === "years" ? repeatEvery * 12 : repeatEvery;
  return isoOf(dayIn(y, m - 1 + months, d));
};

// Reads a bill's stored frequency, defaulting older bills (saved before this existed) to "once".
export const frequencyOf = (bill) => ({
  repeatUnit: bill && bill.repeatUnit ? bill.repeatUnit : "once",
  repeatEvery: bill && bill.repeatEvery ? bill.repeatEvery : 1,
});
